import { InlineIcon } from '@iconify/react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import ReactPDF, { BlobProvider, PDFDownloadLink } from '@react-pdf/renderer';
import React, { FC, useCallback } from 'react';
import Modal from 'src/components/Modal';
import Button from 'src/components/design-system/Button';
import Checkbox from 'src/components/design-system/Checkbox';
import useBoolean from 'src/hooks/useBoolean';

import { SetState } from 'src/types/generic';
interface PrintDetailsProp {
  pdf: React.ReactElement<ReactPDF.DocumentProps, string | React.JSXElementConstructor<any>>;
  setState: SetState<{
    history: boolean;
    notes: boolean;
    attachments: boolean;
  }>;
  state: {
    history: boolean;
    notes: boolean;
    attachments: boolean;
  };
  title: string;
  documentNumber: string;
  showAttachment?: boolean;
  isPoHistoryLoading?: boolean;
  isPrintLoading?: boolean;
  setIsPrintLoading?: SetState<boolean>;
  addDocuments?: () => void;
}

const PrintDetails: FC<PrintDetailsProp> = ({
  pdf,
  setState,
  state,
  title,
  documentNumber,
  showAttachment = true,
  isPoHistoryLoading,
  isPrintLoading,
  setIsPrintLoading,
  addDocuments,
}) => {
  const { value: open, setFalse: closeDialog, setTrue: openDialog } = useBoolean();
  const handleChange = useCallback(
    (key: 'notes' | 'history' | 'attachments') => () => {
      setState((prev) => ({ ...prev, [key]: !prev[key] }));
      if (key === 'attachments' && addDocuments) {
        setTimeout(() => addDocuments(), 100);
      }
    },
    [addDocuments, setState]
  );

  const handlePrintClick = React.useCallback(
    (url: string | null) => () => {
      if (url) {
        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        iframe.style.display = 'none';
        iframe.src = url;
        iframe.onload = function () {
          setTimeout(function () {
            iframe.focus();
            if (iframe.contentWindow) {
              iframe.contentWindow.print();
            }
          }, 1);
        };
      }
    },
    []
  );
  const handleCloseModal = useCallback(() => {
    setState((prev) => ({ ...prev, history: false, notes: false, attachments: false }));
    setIsPrintLoading?.(false);
    closeDialog();
  }, [closeDialog, setIsPrintLoading, setState]);
  return (
    <>
      <Button
        color="secondary"
        startIcon={<InlineIcon icon="material-symbols:print-rounded" />}
        onClick={openDialog}
      >
        Print
      </Button>
      <Modal visible={open} closeModal={handleCloseModal} title={'Print'}>
        {isPoHistoryLoading || isPrintLoading ? (
          <div className="w-full h-24 flex items-center justify-center ">
            <span className="k-icon k-i-loading text-xl" />
          </div>
        ) : (
          <div>
            <p className="font-bold">
              Use the options below to print the current contents of your {title}.
            </p>
            <div className="p-4">
              <Checkbox
                label="Show History"
                checked={state.history}
                onChange={handleChange('history')}
              />
              <Checkbox label="Show Notes" checked={state.notes} onChange={handleChange('notes')} />
              {showAttachment && (
                <Checkbox
                  label={'Include Attachments'}
                  checked={state?.attachments}
                  onChange={handleChange('attachments')}
                />
              )}
            </div>
          </div>
        )}

        <DialogActionsBar>
          <div className="flex flex-row gap-2 justify-end">
            <BlobProvider document={pdf}>
              {({ url, error, loading }) => (
                <Button
                  color="info"
                  disabled={!!error}
                  onClick={handlePrintClick(url)}
                  loading={isPoHistoryLoading || isPrintLoading || loading}
                >
                  Print
                </Button>
              )}
            </BlobProvider>
            <PDFDownloadLink document={pdf} fileName={`iBuyEfficient ${title} ${documentNumber}`}>
              {({ error, loading }) => (
                <Button
                  color="info"
                  disabled={!!error}
                  loading={isPoHistoryLoading || isPrintLoading || loading}
                >
                  Save as PDF
                </Button>
              )}
            </PDFDownloadLink>
            <Button color="info" onClick={handleCloseModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>
    </>
  );
};

export default PrintDetails;
